import { useParams } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';
import { SEO } from '@/components/SEO';
import { useBlueprintPoll } from '@/hooks/useBlueprintPoll';
import { BLUEPRINT_PAGE } from '@/constants/testIds';
import NotFound from '@/pages/NotFound';
import { BlueprintLoadingState, BlueprintComposingState, BlueprintFailedState } from '@/components/blueprint/BlueprintStates';
import { BlueprintHero } from '@/components/blueprint/BlueprintHero';
import {
  SummaryStrip,
  DirectionSection,
  LocationsSection,
  ShotListSection,
  PosesStyleSection,
  TimelineDeliverablesSection,
  BlueprintCTA,
} from '@/components/blueprint/BlueprintSections';

/**
 * Public Creative Blueprint page. Polls while generating, then renders the
 * composed plan section by section.
 */
export default function Blueprint() {
  const { id } = useParams();
  const { bp, notFound } = useBlueprintPoll(id);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Blueprint link copied.');
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  if (notFound) return <NotFound />;
  if (!bp) return <BlueprintLoadingState />;
  if (bp.status === 'generating') return <BlueprintComposingState name={bp.request?.name} />;
  if (bp.status === 'failed' || !bp.content) return <BlueprintFailedState error={bp.error} />;

  const c = bp.content;
  const req = bp.request || {};
  const firstName = (req.name || '').split(' ')[0];

  return (
    <div data-testid={BLUEPRINT_PAGE.root}>
      <SEO title={c.title} description={c.tagline || c.creative_direction?.slice(0, 150)} />
      <BlueprintHero content={c} firstName={firstName} reviewed={bp.status === 'reviewed'} bookingUrl="/book" onCopyLink={copyLink} />

      {bp.section_generating && (
        <div className="bg-[#141416] border-y border-[hsl(var(--lxra-gold))]/30 text-center py-2 text-xs text-[hsl(var(--lxra-muted))]" data-testid="blueprint-section-updating">
          Your photographer is refining the {bp.section_generating.replace('_', ' ')} — this page will update shortly.
        </div>
      )}

      <SummaryStrip request={req} />
      <div className="bg-[#0A0A0A]">
        <DirectionSection content={c} />
        <LocationsSection content={c} />
        <ShotListSection content={c} />
        <PosesStyleSection content={c} />
        <TimelineDeliverablesSection content={c} />
        <BlueprintCTA bookingUrl="/book" onCopyLink={copyLink} />
      </div>
    </div>
  );
}
